import React, { useState } from 'react';
import { TruckIcon, TagIcon, ChevronRightIcon, AwardIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
interface OrderSummaryProps {
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  itemCount: number;
  onOpenCoupons?: () => void;
}
export function OrderSummary({
  subtotal,
  discount,
  tax,
  total,
  itemCount,
  onOpenCoupons
}: OrderSummaryProps) {
  const navigate = useNavigate();
  const [showCoupons, setShowCoupons] = useState(false);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [showShippingBreakdown, setShowShippingBreakdown] = useState(false);

  // Calculate estimated loyalty points (e.g., 1 point per 10 SAR)
  const estimatedPoints = Math.floor((total + 11.99) / 10);
  const shippingFee = 11.99;
  const paymentMethods = ['https://uploadthingy.s3.us-west-1.amazonaws.com/34T2tXtbEwvap5cxibG8Zw/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/tUoQjWm1v91aJ1XmKpKUWr/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/vxtujNwTU2t3ZDV59PkjmC/image.png'];
  const installmentOptions = [{
    provider: 'Tamara',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/34T2tXtbEwvap5cxibG8Zw/image.png',
    installments: 4,
    amount: (total / 4).toFixed(2)
  }, {
    provider: 'Tabby',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tUoQjWm1v91aJ1XmKpKUWr/image.png',
    installments: 4,
    amount: (total / 4).toFixed(2)
  }];
  return <>
      <div className="sticky top-24" data-id="element-1769" data-mp="358qgwyju65c7">
        <div className="bg-white rounded-lg border border-gray-100 p-4 md:p-6" data-id="element-1770" data-mp="30b83ovysd3ye">
          <h2 className="text-xl font-bold mb-6" data-id="element-1771" data-mp="3093zse0w04y4">Order summary</h2>
          
          {/* Order Summary */}
          <div className="space-y-4 mb-6" data-id="element-1772" data-mp="1n14mv7dynsj">
            <div className="flex justify-between text-sm items-center" data-id="element-1773" data-mp="3syuhsy5n3qzi">
              <span className="text-gray-600 flex items-center gap-1" data-id="element-1774" data-mp="1r2lkmoilnrs3">
                Total ({itemCount} item) 
                <button onClick={() => setShowDiscountBreakdown(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center" data-id="element-1775" data-mp="1v46vk4bkpvzn">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-1776" data-mp="1hca7qcgxu892" /></button>
              </span>
              <span className="font-medium" data-id="element-1777" data-mp="1ma2bq4uuxktm">{subtotal.toFixed(2)} SAR</span>
            </div>
            <div className="flex justify-between text-sm items-center" data-id="element-1778" data-mp="17dkwh2jjrm1s">
              <span className="text-gray-600 flex items-center gap-1" data-id="element-1779" data-mp="1cdpi16zbb6yv">
                Shipping Fees 
                <button onClick={() => setShowShippingBreakdown(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center" data-id="element-1780" data-mp="yeg46dykwoy2">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-1781" data-mp="13d138a23h2kx" /></button>
              </span>
              <span className="font-medium" data-id="element-1782" data-mp="2ss8pr5u3g4mf">{shippingFee} SAR</span>
            </div>
            
            <div className="pt-4 border-t border-gray-100" data-id="element-1783" data-mp="2xqa7nnfs9xwb">
              <div className="flex justify-between items-end" data-id="element-1784" data-mp="37txl1s1c7bns">
                <span className="font-bold text-[#1E2B55] text-lg" data-id="element-1785" data-mp="37scoxxzdjknt">Grand Total <span className="text-xs font-normal text-gray-500 block" data-id="element-1786" data-mp="32wxhpovvgn0d">Including VAT</span></span>
                <span className="text-xl font-bold text-[#1E2B55]" data-id="element-1787" data-mp="3golpjfz38pbj">
                  {(total + shippingFee).toFixed(2)} SAR
                </span>
              </div>
            </div>
            
            {discount > 0 && <div className="bg-gray-50 rounded-lg p-2.5 flex items-center gap-2 text-gray-500 text-xs" data-id="element-1788" data-mp="3qn1i7ndrk6n5">
                <div className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-1789" data-mp="3lohx1p6zuyvu">i</div>
                <span data-id="element-1790" data-mp="3vkkwuoedilfm">{discount.toFixed(2)} SAR Saved on this order!</span>
              </div>}
          </div>

          {/* Loyalty Points Preview */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 flex items-center gap-3" data-id="element-1791" data-mp="259yeswel6iu1">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-1792" data-mp="218mxde5v93wx">
              <AwardIcon className="w-4 h-4 text-blue-600" data-id="element-1793" data-mp="27mishghfdw10" />
            </div>
            <div data-id="element-1794" data-mp="27on0oamcxh73">
              <p className="text-sm font-medium text-gray-700" data-id="element-1795" data-mp="2bo47b7diskdu">
                You may earn <span className="font-bold text-[#1E2B55]" data-id="element-1796" data-mp="2bm18bxue3gwz">{estimatedPoints} lakum points</span> with this order
              </p>
            </div>
          </div>

          {/* Complete Purchase Button */}
          <button onClick={() => navigate('/checkout')} className="w-full bg-[#1E2B55] text-white py-3 rounded-lg font-bold hover:bg-[#162041] transition-colors mb-4 shadow-md hover:shadow-lg" data-id="element-1797" data-mp="1jv4vuyc8rea8">
            Checkout
          </button>

          {/* Payment Methods */}
          <div className="flex items-center justify-center gap-2 flex-wrap mt-4" data-id="element-1798" data-mp="1xq78e4blg1w0">
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/mada.svg?v=2.6.2" alt="Mada" className="h-4" data-id="element-1799" data-mp="106w6sx5zvoey" />
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/visa.svg?v=2.6.2" alt="Visa" className="h-4" data-id="element-1800" data-mp="155pc42b4o941" />
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/master-card.svg?v=2.6.2" alt="Mastercard" className="h-4" data-id="element-1801" data-mp="sppm9kmwnct1" />
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/apple-pay.svg?v=2.6.2" alt="Apple Pay" className="h-4" data-id="element-1802" data-mp="npboqwixjl99" />
            <span className="text-[10px] font-bold border border-gray-200 px-1.5 py-0.5 rounded text-gray-600" data-id="element-1803" data-mp="johyvxlxfwdq">CASH</span>
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/tamara.png?v=2.6.2" alt="Tamara" className="h-4" data-id="element-1804" data-mp="erhudr4q71oq" />
          </div>
        </div>
      </div>

      {/* Discount Breakdown Bottom Sheet / Modal */}
      <AnimatePresence data-id="element-1805" data-mp="125a196v9la">
        {showDiscountBreakdown && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowDiscountBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-1806" data-mp="3w4bsntrmfd09" />
            <motion.div initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: '100%',
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-1807" data-mp="3dgb3xnieqgm7">
              <div className="flex items-center justify-between mb-5" data-id="element-1808" data-mp="545em161tfgp">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1809" data-mp="3mhrdxu5b8gpw">Discount breakdown</h3>
                <button onClick={() => setShowDiscountBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-1810" data-mp="3rglv6rnmpx7w">
                  <XIcon className="w-5 h-5" data-id="element-1811" data-mp="j0ix9dmbjmwr" />
                </button>
              </div>
              
              <div className="space-y-3" data-id="element-1812" data-mp="ntvcix53yhcu">
                <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-1813" data-mp="28ifsvdftip67">
                  <span data-id="element-1814" data-mp="23jw5r9phn7c9">Subtotal ({itemCount} item) Excluding VAT</span>
                  <span className="flex-shrink-0" data-id="element-1815" data-mp="23hs4kziz4mfo">{(subtotal + discount).toFixed(2)} SAR</span>
                </div>
                <div className="flex justify-between text-sm text-gray-900 font-medium gap-4" data-id="element-1816" data-mp="2hd3xr2hhe4uh">
                  <span className="uppercase" data-id="element-1817" data-mp="2dc1y4kjnjqrz">ONLINE EXCLUSIVE _ Save 50%</span>
                  <span className="flex-shrink-0" data-id="element-1818" data-mp="1pkd9pf9ybmgb">-{discount.toFixed(2)} SAR</span>
                </div>
                
                <div className="border-t border-dashed border-gray-200 pt-3 mt-1" data-id="element-1819" data-mp="1knd4t7ou8pqs">
                  <div className="flex justify-between text-sm font-bold text-gray-900 mb-2 gap-4" data-id="element-1820" data-mp="1yiox78fiu44j">
                    <span data-id="element-1821" data-mp="1tnt74dtuhqu2">Total ({itemCount} item) Excluding VAT</span>
                    <span className="flex-shrink-0" data-id="element-1822" data-mp="39ynajohqfcbk">{(subtotal - tax).toFixed(2)} SAR</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-1823" data-mp="350lvdenffx5d">
                    <span data-id="element-1824" data-mp="3p49okv7n58wa">+item VAT (15%)</span>
                    <span className="flex-shrink-0" data-id="element-1825" data-mp="3u22tlkx7jwuz">{tax.toFixed(2)} SAR</span>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-3 mt-1" data-id="element-1826" data-mp="1x7ho51l2811">
                  <div className="flex justify-between font-bold text-gray-900 text-base gap-4" data-id="element-1827" data-mp="6pijxejsq5wr">
                    <span data-id="element-1828" data-mp="bobvh1fivmua">Total ({itemCount} item) Including VAT</span>
                    <span className="flex-shrink-0" data-id="element-1829" data-mp="bqg05ll8wpvm">{subtotal.toFixed(2)} SAR</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Shipping Breakdown Bottom Sheet / Modal */}
      <AnimatePresence data-id="element-1830" data-mp="pnbbxlsvsvfd">
        {showShippingBreakdown && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowShippingBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-1831" data-mp="2neo1mnzxeo3b" />
            <motion.div initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: '100%',
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-1832" data-mp="2f58iggnx5i3l">
              <div className="flex items-center justify-between mb-5" data-id="element-1833" data-mp="2tt3bjy5u9oh7">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1834" data-mp="2ourzrhvzh3wc">Shipping breakdown</h3>
                <button onClick={() => setShowShippingBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-1835" data-mp="2ox4frpi6l46r">
                  <XIcon className="w-5 h-5" data-id="element-1836" data-mp="1df3gp1luop0t" />
                </button>
              </div>
              
              <div className="space-y-6" data-id="element-1837" data-mp="1jy40oz12ww7a">
                {/* Shipment 1 */}
                <div data-id="element-1838" data-mp="1ezizoxe068hs">
                  <div className="inline-flex items-center gap-1 bg-[#4ADE80] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-1839" data-mp="1f0c1rl20o9tl">
                    <TruckIcon className="w-3 h-3" data-id="element-1840" data-mp="1ss8otaotvtj4" /> Express
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-1841" data-mp="1nzfmsukquzaq">
                    <span data-id="element-1842" data-mp="22o27lbt54j9c">1x Express delivery item(s)</span>
                    <span className="font-bold flex-shrink-0" data-id="element-1843" data-mp="1xqakcv3fxlbh">0 SAR</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2" data-id="element-1844" data-mp="2bptiqw6g9ial">
                    <div className="h-full bg-[#84cc16] w-[30%] rounded-full" data-id="element-1845" data-mp="27o869bxtv60x"></div>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-1846" data-mp="hbiq0cmqi3zj">
                    Spend <span className="font-bold text-gray-900" data-id="element-1847" data-mp="hc293wo4eyf5">92.50 SAR</span> more to get FREE shipping on Express items.
                  </p>
                </div>

                <div className="border-t border-gray-100" data-id="element-1848" data-mp="37bd4xt4xair"></div>

                {/* Shipment 2 */}
                <div data-id="element-1849" data-mp="76slq03uyns5">
                  <div className="inline-flex items-center gap-1 bg-[#1E2B55] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-1850" data-mp="77c056uauu1f">
                    <TruckIcon className="w-3 h-3" data-id="element-1851" data-mp="3ukxv0aifynv3" /> Standard
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-1852" data-mp="3uil7zixttnb6">
                    <span data-id="element-1853" data-mp="3fprrpvaqd35g">1x Standard delivery item(s)</span>
                    <span className="font-bold flex-shrink-0" data-id="element-1854" data-mp="3kntccknt1aj1">{shippingFee} SAR</span>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-1855" data-mp="36oaauapi0xfp">
                    Estimated delivery: 2-3 business days
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}